import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '../../../services/api';
import { Button, Pagination } from '../../common';
import SynapseEntityModal from './SynapseEntityModal';
import './Synapses.css';

const PAGE_SIZE = 50;

const SynapsesTable = ({ 
  emptyMessage = 'No connections available.', 
  searchPlaceholder = 'Search by source node, edge, or target node...',
  onSynapsesChange,
  graphId,
  disableDrilldowns = false,
  // New props for Change Nodes/Edges page
  searchQuery: externalSearchQuery,
  showDeleted = false,
  selectedSynapses,
  onSelectionChange,
  onDelete,
  onRestore,
  onSynapseClick,
  enableSelection = false,
  enableDelete = false,
  requireExplicitSearch = false,
}) => {
  const location = useLocation();
  const [synapses, setSynapses] = useState([]);
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSynapse, setSelectedSynapse] = useState(null);
  const [modalTab, setModalTab] = useState('source');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Request counter to prevent stale responses from overwriting newer data
  const requestIdRef = useRef(0);

  // Use external search query if provided, otherwise use internal
  const searchQuery = externalSearchQuery !== undefined ? externalSearchQuery : internalSearchQuery;

  // Check if current page is expand-graph
  const isExpandGraphPage = location.pathname.includes('expand-graph');

  const fetchSynapses = useCallback(async (query = '', pageNum = 1) => {
    if (!graphId) {
      setError('Graph ID is required to load connections.');
      setSynapses([]);
      return;
    }

    // Increment request counter and capture this request's id
    const currentRequestId = ++requestIdRef.current;

    try {
      setLoading(true);
      setError(null);
      
      // Always use searchSynapses -- it does direct SQL and works for all graph sizes
      // (listSynapses returns empty for graphs exceeding NODE_COUNT_DB_QUERY_LIMIT)
      const response = await apiService.searchSynapses({
        graphId,
        q: query || undefined,
        includeDeleted: showDeleted,
        page: pageNum,
        pageSize: PAGE_SIZE,
      });

      // Only apply results if this is still the latest request
      if (currentRequestId !== requestIdRef.current) return;

      if (response.success) {
        setSynapses(response.synapses || []);
        const total = response.totalCount || 0;
        setTotalCount(total);
        setTotalPages(Math.ceil(total / PAGE_SIZE) || 0);
      } else {
        setSynapses([]);
        setTotalCount(0);
        setTotalPages(0);
        setError(response.message || 'Failed to load connections.');
      }
    } catch (err) {
      // Ignore errors from stale requests
      if (currentRequestId !== requestIdRef.current) return;
      console.error('Failed to load connections:', err);
      setSynapses([]);
      setTotalCount(0);
      setTotalPages(0);
      setError(err.response?.data?.message || 'Failed to load connections.');
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [graphId, showDeleted]);

  // Reset page when search query changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery, showDeleted]);

  useEffect(() => {
    if (requireExplicitSearch && externalSearchQuery === null) return;
    fetchSynapses(searchQuery || '', page);
  }, [fetchSynapses, searchQuery, page, requireExplicitSearch, externalSearchQuery]);

  // Notify parent of changes
  useEffect(() => {
    if (onSynapsesChange) {
      onSynapsesChange(synapses);
    }
  }, [synapses, onSynapsesChange]);

  // Handle synapse updates from navigation state
  useEffect(() => {
    if (location.state?.synapseUpdated) {
      fetchSynapses(searchQuery);
    
      // Clear the state to prevent re-processing
      window.history.replaceState({ ...location.state, synapseUpdated: false }, '');
    }
  }, [location.state, fetchSynapses, searchQuery]);

  // Determine empty state message
  const getEmptyMessage = () => {
    if (requireExplicitSearch && externalSearchQuery === null) {
      return 'Use the search bar above to find synapses.';
    }

    if (error) {
      return error;
    }

    if (loading) {
      return 'Loading connections...';
    }

    if (searchQuery.trim() && synapses.length === 0) {
      return 'No synapses found matching your search.';
    }
    if (synapses.length === 0) {
      return emptyMessage;
    }
    return emptyMessage;
  };

  const openEntityModal = (synapse, tab) => {
    setSelectedSynapse(synapse);
    setModalTab(tab);
    setIsModalOpen(true);
  };

  const closeEntityModal = () => {
    setIsModalOpen(false);
    setSelectedSynapse(null);
  };

  const handleEntityUpdated = () => {
    fetchSynapses(searchQuery);
  };

  const handleAddNewSynapse = () => {
    // Open modal in add mode (no synapse, default to edge tab)
    setSelectedSynapse(null);
    setModalTab('edge');
    setIsModalOpen(true);
  };

  return (
    <>
      <div className="synapses-table-wrapper">
        {/* Header with Add Button */}
        {isExpandGraphPage && (
          <div className="synapses-header">
            <div className="synapses-header-right">
              <Button 
                variant="primary"
                onClick={handleAddNewSynapse}
              >
                + Add New Connection
              </Button>
            </div>
          </div>
        )}
        
        {/* Search Box - only show if not using external search */}
        {externalSearchQuery === undefined && (
          <div className="synapses-search">
            <input
              type="text"
              className="synapses-search-input"
              placeholder={searchPlaceholder}
              value={internalSearchQuery}
              onChange={(e) => setInternalSearchQuery(e.target.value)}
            />
          </div>
        )}

        {/* Loading Overlay */}
        {loading && (
          <div className="table-loading-overlay">
            <div className="loading-spinner"></div>
            <p>Searching connections...</p>
          </div>
        )}

        {/* Table or Empty State */}
        {synapses.length === 0 && !loading ? (
          <div className="synapses-table-container">
            <div className="empty-state">
              <p>{getEmptyMessage()}</p>
            </div>
          </div>
        ) : synapses.length > 0 ? (
          <div className="synapses-table-container">
            <table className="synapses-table">
              <thead>
                <tr>
                  {enableSelection && (
                    <th className="checkbox-column">
                      <input
                        type="checkbox"
                        checked={selectedSynapses && selectedSynapses.size === synapses.length && synapses.length > 0}
                        onChange={(e) => {
                          if (onSelectionChange) {
                            if (e.target.checked) {
                              onSelectionChange(new Set(synapses.map(s => s.synapseid || s.SynapseId || s.id)));
                            } else {
                              onSelectionChange(new Set());
                            }
                          }
                        }}
                      />
                    </th>
                  )}
                  <th>Source Node</th>
                  <th>Edge</th>
                  <th>Target Node</th>
                  {enableDelete && <th className="actions-column">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {synapses.map((synapse) => {
                  const synapseId = synapse.synapseid || synapse.SynapseId || synapse.id;
                  const isDeleted = synapse.deletedAt || synapse.softDeletedAt || synapse.SoftDeletedAt;
                  const isSelected = selectedSynapses && selectedSynapses.has(synapseId);
                  const edgeName = synapse.edgename || synapse.EdgeName || synapse.edgeName;
                  
                  return (
                    <tr key={synapseId} className={isDeleted ? 'deleted-row' : ''}>
                      {enableSelection && (
                        <td className="checkbox-column">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (onSelectionChange) {
                                const newSelection = new Set(selectedSynapses);
                                if (e.target.checked) {
                                  newSelection.add(synapseId);
                                } else {
                                  newSelection.delete(synapseId);
                                }
                                onSelectionChange(newSelection);
                              }
                            }}
                          />
                        </td>
                      )}
                      <td className="synapse-cell">
                        {disableDrilldowns ? (
                          <span className="synapse-cell-text">{synapse.sourcenodename || synapse.sourceNodeName}</span>
                        ) : (
                        <button
                          type="button"
                          className="synapse-drilldown-link"
                          onClick={disableDrilldowns ? undefined : () => openEntityModal(synapse, 'source')}
                          disabled={disableDrilldowns || !(synapse.sourcenodeid || synapse.SourceNodeId || synapse.sourceNodeId)}
                        >
                          {synapse.sourcenodename || synapse.sourceNodeName}
                        </button>
                        )}
                      </td>
                      <td className="synapse-cell">
                        {disableDrilldowns ? (
                          <span className="synapse-cell-text">{edgeName}</span>
                        ) : (
                        <button
                          type="button"
                          className="synapse-drilldown-link"
                          onClick={() => {
                            if (onSynapseClick) {
                              onSynapseClick(synapse);
                            } else {
                              openEntityModal(synapse, 'edge');
                            }
                          }}
                          disabled={disableDrilldowns || !synapseId}
                        >
                          {edgeName}
                        </button>
                        )}
                      </td>
                      <td className="synapse-cell">
                        {disableDrilldowns ? (
                          <span className="synapse-cell-text">{synapse.targetnodename || synapse.targetNodeName}</span>
                        ) : (
                        <button
                          type="button"
                          className="synapse-drilldown-link"
                          onClick={disableDrilldowns ? undefined : () => openEntityModal(synapse, 'target')}
                          disabled={!(synapse.targetnodeid || synapse.TargetNodeId || synapse.targetNodeId)}
                        >
                          {synapse.targetnodename || synapse.targetNodeName}
                        </button>
                        )}
                      </td>
                      {enableDelete && (
                        <td className="actions-column">
                          {isDeleted ? (
                            <button
                              type="button"
                              className="action-link restore-link"
                              onClick={() => onRestore && onRestore(synapseId)}
                            >
                              Restore
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="action-link delete-link"
                              onClick={() => onDelete && onDelete(synapseId, edgeName || 'Synapse')}
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                showPageInfo={true}
                className="synapses-pagination"
              />
            )}
            {totalCount > 0 && (
              <div className="table-result-count">{totalCount} total results</div>
            )}
          </div>
        ) : null}
      </div>
      <SynapseEntityModal
        isOpen={isModalOpen}
        synapse={selectedSynapse}
        defaultTab={modalTab}
        onClose={closeEntityModal}
        onEntityUpdated={handleEntityUpdated}
        graphId={graphId}
      />
    </>
  );
};

export default SynapsesTable;

